import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// A list's filters, kept in the URL. Takes a `{ parse(search), serialize(filters) }` pair (the
// fmdc lists build theirs with makeListUrl; the Score Specs list uses listFilterUrl.js) and returns
// `{ filters, setFilters(patch) }`. One mechanism for every URL-owned list, so a fix to how two
// same-tick patches compose lands everywhere at once.
//
// The URL is the state, not a mirror of it. Writes replace rather than push: back should leave the
// screen, not step through every keystroke of a search. useLocation + useNavigate rather than
// useSearchParams: that hook's functional setter hands back the params its closure rendered with,
// so two patches in one tick would drop the earlier one. The ref below keeps them composing.
export const useUrlFilters = (listUrl) => {
  const { search } = useLocation();
  const navigate = useNavigate();

  const filters = React.useMemo(() => listUrl.parse(search), [listUrl, search]);

  const searchRef = React.useRef(search);
  searchRef.current = search;

  const setFilters = React.useCallback(
    (patch) => {
      const current = listUrl.parse(searchRef.current);
      // Any change to a filter resets paging unless the patch sets offset itself: page 3 of a
      // different filter is not a place.
      const resetsOffset = Object.keys(patch).some((key) => key !== 'offset') && !('offset' in patch);
      const merged = { ...current, ...patch, ...(resetsOffset && 'offset' in current ? { offset: 0 } : {}) };
      const next = listUrl.serialize(merged).toString();
      const nextSearch = next ? `?${next}` : '';
      searchRef.current = nextSearch;
      navigate({ search: nextSearch }, { replace: true });
    },
    [listUrl, navigate]
  );

  return { filters, setFilters };
};
